import { EmptyState, Button, cn } from "@nexus/ui";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type Ref,
} from "react";
import { useTranslation } from "react-i18next";

import { useStatusFilterExpansion } from "@/features/marketplace/hooks/useStatusFilterExpansion";
import { localTodayYmd } from "@/features/marketplace/lib/order-deadline";
import { SortableColumnHeader } from "@/features/marketplace/components/orders-list/SortableColumnHeader";
import { PartnerOrderRow } from "@/features/marketplace/partner/components/PartnerOrderRow";
import type { OrderLineVideo } from "@/features/marketplace/types/marketplace.types";
import { PartnerQueueToolbar } from "@/features/marketplace/partner/components/PartnerQueueToolbar";
import { partnerWorkItemDomId } from "@/features/marketplace/partner/components/PartnerScriptRow";
import {
  PARTNER_QUEUE_CELL_DUE_CLASS,
  PARTNER_QUEUE_CELL_RECEIVED_CLASS,
  PARTNER_QUEUE_CELL_SCRIPTS_CLASS,
  PARTNER_QUEUE_CELL_STATUS_CLASS,
  PARTNER_QUEUE_CELL_TYPE_CLASS,
  PARTNER_QUEUE_GRID_CLASS,
  PARTNER_QUEUE_ROW_PADDING_CLASS,
} from "@/features/marketplace/partner/lib/partner-queue-grid";
import {
  clearPartnerQueueFilters,
  partnerShownVideos,
  partnerStatusFilterExpansion,
  revealPartnerWorkGroup,
  sectionPartnerWorkGroups,
  type PartnerQueueFilters,
  type PartnerQueueSortBy,
  type PartnerQueueSortDirection,
  type PartnerWorkGroup,
  type PartnerWorkItem,
} from "@/features/marketplace/partner/lib/partner-work-queue";

const DEADLINE_GROUP_LABEL_KEY = {
  overdue: "marketplace.orders.list.deadlineGroups.overdue",
  today: "marketplace.orders.list.deadlineGroups.today",
  thisWeek: "marketplace.orders.list.deadlineGroups.thisWeek",
  later: "marketplace.orders.list.deadlineGroups.later",
  unscheduled: "marketplace.orders.list.deadlineGroups.unscheduled",
} as const;

interface PartnerQueueListProps {
  groups: PartnerWorkGroup[];
  query: PartnerQueueFilters;
  clients: Array<{ id: string; name: string }>;
  overdueCount: number;
  totalCount: number;
  selectedScriptId: string | null;
  selectedOrderId: string | null;
  selectedVideoKey: string | null;
  expandedScriptVideoIds: ReadonlySet<string>;
  searchInputRef: Ref<HTMLInputElement>;
  onQueryChange: (next: PartnerQueueFilters) => void;
  onToggleScriptVideosExpanded: (scriptId: string) => void;
  onOpen: (item: PartnerWorkItem) => void;
  onOpenForUpload?: (item: PartnerWorkItem, video?: OrderLineVideo) => void;
}

const EMPTY_REVEALED_ORDER_IDS: ReadonlySet<string> = new Set();

function orderRowId(orderId: string): string {
  return `partner-order-${orderId}`;
}

export function PartnerQueueList({
  groups: filteredGroups,
  query,
  clients,
  overdueCount,
  totalCount,
  selectedScriptId,
  selectedOrderId,
  selectedVideoKey,
  expandedScriptVideoIds: storedExpandedScriptVideoIds,
  searchInputRef,
  onQueryChange,
  onToggleScriptVideosExpanded,
  onOpen,
  onOpenForUpload,
}: PartnerQueueListProps) {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);
  const todayYmd = localTodayYmd();
  // Orders opened in full from a "hidden by filter" note. Tied to the Status
  // selection it was made under, so changing or clearing the filter drops it.
  const statusFilterKey = query.statuses.join(",");
  const [revealed, setRevealed] = useState<{
    statusFilterKey: string;
    orderIds: ReadonlySet<string>;
  }>(() => ({ statusFilterKey: "", orderIds: new Set() }));
  const revealedOrderIds =
    revealed.statusFilterKey === statusFilterKey
      ? revealed.orderIds
      : EMPTY_REVEALED_ORDER_IDS;
  // Clearing the filter ends every reveal, so re-applying it starts filtered.
  useEffect(() => {
    if (statusFilterKey === "") {
      setRevealed({ statusFilterKey: "", orderIds: EMPTY_REVEALED_ORDER_IDS });
    }
  }, [statusFilterKey]);
  const revealOrder = (orderId: string) => {
    setRevealed((current) => ({
      statusFilterKey,
      orderIds: new Set(
        current.statusFilterKey === statusFilterKey ? current.orderIds : [],
      ).add(orderId),
    }));
  };
  const groups = useMemo(
    () =>
      filteredGroups.map((group) =>
        revealedOrderIds.has(group.order.id)
          ? revealPartnerWorkGroup(group)
          : group,
      ),
    [filteredGroups, revealedOrderIds],
  );
  const sections = useMemo(
    () => sectionPartnerWorkGroups(groups, query.groupBy, todayYmd),
    [groups, query.groupBy, todayYmd],
  );

  const [storedExpandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const statusFilterActive = query.statuses.length > 0;
  const filterExpansion = useMemo(
    () => partnerStatusFilterExpansion(groups, statusFilterActive),
    [groups, statusFilterActive],
  );
  const toggleStoredExpanded = useCallback((orderId: string) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  }, []);
  const orderExpansion = useStatusFilterExpansion({
    statusFilterKey,
    stored: storedExpandedIds,
    autoExpanded: filterExpansion.orderIds,
    onToggleStored: toggleStoredExpanded,
  });
  const scriptVideoExpansion = useStatusFilterExpansion({
    statusFilterKey,
    stored: storedExpandedScriptVideoIds,
    autoExpanded: filterExpansion.scriptIds,
    onToggleStored: onToggleScriptVideosExpanded,
  });
  const expandedIds = orderExpansion.expandedIds;
  const expandedScriptVideoIds = scriptVideoExpansion.expandedIds;
  const [activeRowId, setActiveRowId] = useState<string | null>(
    selectedOrderId ? orderRowId(selectedOrderId) : null,
  );

  useEffect(() => {
    if (!selectedOrderId) return;
    setExpandedIds((current) => {
      if (current.has(selectedOrderId)) return current;
      return new Set([...current, selectedOrderId]);
    });
  }, [selectedOrderId]);

  useEffect(() => {
    if (document.activeElement === document.body) {
      listRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (
        event.key !== "j" &&
        event.key !== "k" &&
        event.key !== "ArrowDown" &&
        event.key !== "ArrowUp"
      ) {
        return;
      }
      if (isQueueChromeTarget(event.target)) return;
      const rows = Array.from(
        listRef.current?.querySelectorAll<HTMLElement>("[data-tree-row]") ?? [],
      );
      if (rows.length === 0) return;
      const active = document.activeElement;
      const currentIndex = rows.findIndex(
        (row) => row === active || row.contains(active),
      );
      const delta = event.key === "j" || event.key === "ArrowDown" ? 1 : -1;
      const nextIndex =
        currentIndex === -1
          ? delta === 1
            ? 0
            : rows.length - 1
          : (currentIndex + delta + rows.length) % rows.length;
      event.preventDefault();
      rows[nextIndex]?.focus();
      setActiveRowId(rows[nextIndex]?.id ?? null);
    };
    window.addEventListener("keydown", handleKeyDown, { capture: true });
    return () =>
      window.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, []);

  const treeRowIds = useMemo(() => {
    const ids: string[] = [];
    for (const section of sections) {
      for (const group of section.groups) {
        ids.push(orderRowId(group.order.id));
        if (expandedIds.has(group.order.id)) {
          for (const item of group.items) {
            ids.push(partnerWorkItemDomId(item));
            if (expandedScriptVideoIds.has(item.script.id)) {
              for (const video of partnerShownVideos(item)) {
                ids.push(
                  `partner-video-${item.script.id}-${video.id ?? video.position}`,
                );
              }
            }
          }
        }
      }
    }
    return ids;
  }, [expandedIds, expandedScriptVideoIds, sections]);

  const rovingActiveId =
    activeRowId && treeRowIds.includes(activeRowId)
      ? activeRowId
      : (treeRowIds[0] ?? null);

  const toggleExpanded = orderExpansion.toggle;

  const handleSortChange = (
    sortBy: PartnerQueueSortBy,
    sortDirection: PartnerQueueSortDirection,
  ) => {
    onQueryChange({ ...query, sortBy, sortDirection });
  };

  const handleListKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === "/") {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      event.preventDefault();
      if (searchInputRef && typeof searchInputRef !== "function") {
        searchInputRef.current?.focus();
      }
      return;
    }
    if (!(event.target instanceof HTMLElement)) return;
    const currentRow = event.target.closest<HTMLElement>("[data-tree-row]");
    if (!currentRow || event.target !== currentRow) return;
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      return;
    }
    const rows = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>("[data-tree-row]") ?? [],
    );
    if (rows.length === 0) return;
    const currentIndex = rows.indexOf(currentRow);
    let nextIndex: number;
    switch (event.key) {
      case "ArrowDown":
        nextIndex =
          currentIndex < 0 ? 0 : Math.min(rows.length - 1, currentIndex + 1);
        break;
      case "ArrowUp":
        nextIndex =
          currentIndex < 0 ? rows.length - 1 : Math.max(0, currentIndex - 1);
        break;
      case "Home":
        nextIndex = 0;
        break;
      default:
        nextIndex = rows.length - 1;
    }
    event.preventDefault();
    rows[nextIndex]?.focus();
    setActiveRowId(rows[nextIndex]?.id ?? null);
  };

  const sectionLabel = (key: string): string => {
    if (query.groupBy === "client") {
      return clients.find((client) => client.id === key)?.name ?? key;
    }
    if (query.groupBy === "deadline") {
      return t(
        DEADLINE_GROUP_LABEL_KEY[key as keyof typeof DEADLINE_GROUP_LABEL_KEY],
      );
    }
    return "";
  };

  let rowIndex = 2;

  return (
    <div
      ref={listRef}
      tabIndex={-1}
      className="flex min-w-0 flex-col gap-3"
      onKeyDown={handleListKeyDown}
    >
      <div className="sticky top-0 z-20 -mx-1 bg-background/95 px-1 py-2 backdrop-blur-sm">
        <PartnerQueueToolbar
          query={query}
          clients={clients}
          overdueCount={overdueCount}
          shownCount={groups.length}
          totalCount={totalCount}
          onChange={onQueryChange}
          searchInputRef={searchInputRef}
        />
      </div>

      {groups.length === 0 ? (
        <EmptyState
          icon="search"
          title={t(
            "marketplace.partner.emptyState.title",
            "No work in this view",
          )}
          description={t(
            "marketplace.partner.emptyState.body",
            "Try another queue view or clear the filters.",
          )}
          className="rounded-md border border-dashed border-border py-10"
          action={
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onQueryChange(clearPartnerQueueFilters(query))}
            >
              {t("marketplace.partner.filters.clear", "Clear filters")}
            </Button>
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-md">
          <div
            role="treegrid"
            aria-label={t(
              "marketplace.partner.list.treegridLabel",
              "Production orders and their scripts",
            )}
            aria-rowcount={treeRowIds.length + 1}
            aria-colcount={8}
            className="min-w-full overflow-hidden rounded-md border border-border bg-card"
          >
            <div
              role="row"
              aria-rowindex={1}
              className={cn(
                PARTNER_QUEUE_GRID_CLASS,
                PARTNER_QUEUE_ROW_PADDING_CLASS,
                "h-8 border-b border-border bg-muted/30 text-[11px] font-medium uppercase tracking-wide text-muted-foreground",
              )}
            >
              <span role="columnheader" />
              <SortableColumnHeader
                label={t("marketplace.partner.columns.client", "Client")}
                sortBy="client"
                activeSortBy={query.sortBy}
                sortDirection={query.sortDirection}
                onChange={handleSortChange}
              />
              <span
                role="columnheader"
                className={PARTNER_QUEUE_CELL_TYPE_CLASS}
              >
                {t("marketplace.partner.columns.type", "Type")}
              </span>
              <SortableColumnHeader
                label={t("marketplace.orders.list.columns.scripts")}
                sortBy="scripts"
                activeSortBy={query.sortBy}
                sortDirection={query.sortDirection}
                onChange={handleSortChange}
                className={PARTNER_QUEUE_CELL_SCRIPTS_CLASS}
              />
              <SortableColumnHeader
                label={t("marketplace.partner.columns.status")}
                sortBy="status"
                activeSortBy={query.sortBy}
                sortDirection={query.sortDirection}
                onChange={handleSortChange}
                className={PARTNER_QUEUE_CELL_STATUS_CLASS}
              />
              <SortableColumnHeader
                label={t("marketplace.partner.columns.received", "Received")}
                sortBy="received"
                activeSortBy={query.sortBy}
                sortDirection={query.sortDirection}
                defaultDirection="desc"
                onChange={handleSortChange}
                className={PARTNER_QUEUE_CELL_RECEIVED_CLASS}
              />
              <SortableColumnHeader
                label={t("marketplace.partner.columns.due")}
                sortBy="deadline"
                activeSortBy={query.sortBy}
                sortDirection={query.sortDirection}
                onChange={handleSortChange}
                className={PARTNER_QUEUE_CELL_DUE_CLASS}
              />
              <span role="columnheader" />
            </div>

            {sections.map((section) => (
              <section
                key={section.key}
                role="rowgroup"
                aria-label={
                  query.groupBy === "none"
                    ? undefined
                    : sectionLabel(section.key)
                }
              >
                {query.groupBy !== "none" ? (
                  <div className="border-b border-border bg-muted/20 px-3 py-1.5 text-xs font-medium text-muted-foreground">
                    {sectionLabel(section.key)}
                  </div>
                ) : null}
                <ul role="presentation">
                  {section.groups.map((group) => {
                    const currentIndex = rowIndex;
                    let childRows = 0;
                    if (expandedIds.has(group.order.id)) {
                      for (const item of group.items) {
                        childRows += 1;
                        if (expandedScriptVideoIds.has(item.script.id)) {
                          childRows += partnerShownVideos(item).length;
                        }
                      }
                    }
                    rowIndex += 1 + childRows;
                    return (
                      <PartnerOrderRow
                        key={group.order.id}
                        group={group}
                        expanded={expandedIds.has(group.order.id)}
                        selectedScriptId={
                          selectedOrderId === group.order.id
                            ? selectedScriptId
                            : null
                        }
                        selectedVideoKey={
                          selectedOrderId === group.order.id
                            ? selectedVideoKey
                            : null
                        }
                        density={query.density}
                        activeRowId={rovingActiveId}
                        rowIndex={currentIndex}
                        expandedScriptVideoIds={expandedScriptVideoIds}
                        onRevealStatusHidden={() => revealOrder(group.order.id)}
                        onToggleExpanded={() => toggleExpanded(group.order.id)}
                        onToggleScriptVideosExpanded={
                          scriptVideoExpansion.toggle
                        }
                        onOpen={onOpen}
                        onOpenForUpload={onOpenForUpload}
                      />
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground">
        {t(
          "marketplace.partner.list.keyboardHint",
          "↑ ↓ or J K move · ← → expand · Enter open · / search · Esc close",
        )}
      </p>
    </div>
  );
}

function isQueueChromeTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(
    target.closest(
      "button, a[href], input, select, textarea, [contenteditable='true'], [role='button'], [role='link'], [role='textbox'], [role='combobox'], [role='menuitem']",
    ),
  );
}
